<section class="ai-section">
    <div class="container">
        <div class="card">
            <div class="page-header">
                <button class="icon-btn back" title="Back to agents" (click)="back()"><i class="fa-solid fa-arrow-left"></i></button>
                <div>
                    <h3 class="page-title" dir="auto">{{ isNew ? 'Create agent' : (agent?.name || 'Agent') }}</h3>
                    <p class="page-subtitle" *ngIf="agent">
                        <span class="badge" [ngClass]="agent.status === 'ACTIVE' ? 'ok' : agent.status === 'PAUSED' ? 'warn' : 'draft'">{{ agent.status }}</span>
                        <span class="mono"> ID {{ agent.id }}</span>
                    </p>
                    <p class="page-subtitle" *ngIf="isNew">Fill in the settings and the identity, then save. Channels and the test chat open after the first save.</p>
                </div>
                <div class="flex-spacer"></div>
                <span *ngIf="savedNote" class="badge ok"><i class="fa-solid fa-check"></i> {{ savedNote }}</span>
            </div>

            <div class="editor-tabs" role="tablist">
                <button role="tab" [class.active]="tab === 'settings'" (click)="setTab('settings')"><i class="fa-solid fa-sliders"></i> Settings</button>
                <button role="tab" [class.active]="tab === 'identity'" (click)="setTab('identity')"><i class="fa-solid fa-id-card"></i> Identity</button>
                <button role="tab" *ngIf="!isNew" [class.active]="tab === 'channels'" (click)="setTab('channels')"><i class="fa-solid fa-code"></i> Channels</button>
                <button role="tab" *ngIf="!isNew && perms.has(P.playbookList)" id="ag-playbooks-tab" [class.active]="tab === 'playbooks'" (click)="setTab('playbooks')"><i class="fa-solid fa-book-open"></i> Playbooks</button>
                <button role="tab" *ngIf="!isNew" id="ag-tools-tab" [class.active]="tab === 'tools'" (click)="setTab('tools')"><i class="fa-solid fa-screwdriver-wrench"></i> Tools</button>
                <button role="tab" *ngIf="!isNew" id="ag-automations-tab" [class.active]="tab === 'automations'" (click)="setTab('automations')"><i class="fa-solid fa-clock"></i> Automations</button>
                <button role="tab" *ngIf="!isNew && perms.has(P.agentTest)" [class.active]="tab === 'test'" (click)="setTab('test')"><i class="fa-solid fa-comment-dots"></i> Test chat</button>
            </div>

            <!-- SETTINGS -->
            <form *ngIf="tab === 'settings'" class="tab-body" (ngSubmit)="saveSettings()">
                <fieldset [disabled]="!canEdit" class="plain-fieldset">
                    <div class="two-col">
                        <div class="form-field">
                            <label for="ag-project-id">Project</label>
                            <input id="ag-project-id" name="projectId" [(ngModel)]="form.projectId" list="ai-project-options"
                                [readonly]="!isNew" placeholder="Project ID (UUID)" />
                            <datalist id="ai-project-options">
                                <option *ngFor="let p of projects" [value]="p.projectId">{{ p.name }}</option>
                            </datalist>
                            <span class="hint" *ngIf="!isNew">The project of an agent cannot change.</span>
                        </div>
                        <div class="form-field">
                            <label for="ag-name">Name</label>
                            <input id="ag-name" name="name" [(ngModel)]="form.name" maxlength="160" dir="auto" placeholder="Business or assistant name" />
                        </div>
                    </div>

                    <div class="two-col">
                        <div class="form-field">
                            <label for="ag-avatar">Picture</label>
                            <div class="copy-row avatar-row">
                                <img *ngIf="form.avatarUrl; else noAvatar" [src]="form.avatarUrl" alt="" class="avatar-preview" id="ag-avatar-img" />
                                <ng-template #noAvatar><span class="avatar-preview avatar-initial" aria-hidden="true">{{ (form.name || '?').charAt(0).toUpperCase() }}</span></ng-template>
                                <input id="ag-avatar" name="avatarUrl" [(ngModel)]="form.avatarUrl" maxlength="1000" placeholder="https://... (or upload)" />
                            </div>
                            <div class="copy-row" *ngIf="!isNew && canEdit">
                                <input #avatarFile type="file" class="visually-hidden" [accept]="avatarAccept" (change)="onAvatarPicked($event)" id="ag-avatar-file" />
                                <button type="button" class="btn-light btn-inline" id="ag-avatar-upload" [disabled]="avatarBusy" (click)="avatarFile.click()">
                                    <i class="fa-solid fa-upload"></i> {{ avatarBusy ? 'Uploading...' : 'Upload picture' }}
                                </button>
                                <button type="button" class="btn-light btn-inline" id="ag-avatar-remove" *ngIf="agent?.avatarUrl" [disabled]="avatarBusy" (click)="removeAvatar()">
                                    <i class="fa-solid fa-trash"></i> Remove
                                </button>
                            </div>
                            <span class="hint" *ngIf="!avatarError">PNG, JPEG or WebP up to 2 MB, stored by the agent service. {{ isNew ? 'Upload after the first save.' : '' }}</span>
                            <span class="error-text" *ngIf="avatarError" id="ag-avatar-error">{{ avatarError }}</span>
                        </div>
                        <div class="form-field">
                            <label for="ag-model">Model</label>
                            <p-dropdown inputId="ag-model" name="modelId" [ngModel]="form.modelId" (ngModelChange)="onModelChange($event)" [options]="modelGroups" [group]="true"
                                optionLabel="label" optionValue="value" placeholder="Select an enabled model" [filter]="true"
                                filterBy="label" styleClass="w-full" appendTo="body" [disabled]="!canEdit">
                                <ng-template let-group pTemplate="group">
                                    <strong>{{ group.label }}</strong>
                                </ng-template>
                            </p-dropdown>
                            <span class="hint" *ngIf="modelGroups.length === 0">No enabled models. Add or enable one under LLM Models.</span>
                        </div>
                    </div>

                    <div class="three-col">
                        <div class="form-field">
                            <label for="ag-temp">Temperature (0 to 2)</label>
                            <input id="ag-temp" name="temperature" type="number" min="0" max="2" step="0.1" [(ngModel)]="form.temperature"
                                [disabled]="selectedModel?.supportsTemperature === false" />
                            <span class="hint" *ngIf="selectedModel?.supportsTemperature === false">This model does not take a temperature; it is not sent.</span>
                        </div>
                        <div class="form-field">
                            <label for="ag-maxout">Max output tokens</label>
                            <input id="ag-maxout" name="maxOutputTokens" type="number" min="1" [(ngModel)]="form.maxOutputTokens"
                                [placeholder]="selectedModel?.maxOutputTokens ? 'model max ' + selectedModel?.maxOutputTokens : ''" />
                        </div>
                        <div class="form-field">
                            <label for="ag-history">History limit (messages)</label>
                            <input id="ag-history" name="historyLimit" type="number" min="0" [(ngModel)]="form.historyLimit" />
                        </div>
                    </div>

                    <div class="form-field" *ngIf="thinkingLevels.length">
                        <label for="ag-thinking">Thinking level</label>
                        <select id="ag-thinking" name="reasoningEffort" [(ngModel)]="form.reasoningEffort">
                            <option value="">{{ thinkingDefaultLabel }}</option>
                            <option *ngFor="let l of thinkingLevels" [value]="l.value">{{ l.label }}</option>
                        </select>
                        <span class="hint">Sent to the provider as reasoning_effort. Deeper thinking is slower and costs more.</span>
                    </div>

                    <div class="form-field">
                        <label class="section-title" style="font-size: 15px">Options</label>
                        <span class="hint">Each option must also be allowed by the project plan; the customer can change them in the AI Assistant.</span>
                    </div>
                    <div class="two-col">
                        <div class="form-field">
                            <label for="ag-reply-delay">Reply delay (ms)</label>
                            <input id="ag-reply-delay" name="replyDelayMs" type="number" min="0" [max]="replyDelayMax" step="100" [(ngModel)]="form.replyDelayMs" />
                            <span class="hint">0 to {{ replyDelayMax }}. The agent waits this long after a message, so messages sent in a row get one answer.</span>
                        </div>
                        <div class="form-field">
                            <label for="ag-voice-name">Voice</label>
                            <select *ngIf="voices.length; else voiceText" id="ag-voice-name" name="voiceName" [(ngModel)]="form.voiceName" [disabled]="!form.voiceOutputEnabled">
                                <option value="">Default voice{{ defaultVoice ? ' (' + defaultVoice + ')' : '' }}</option>
                                <option *ngFor="let v of voices" [value]="v">{{ v }}</option>
                                <option *ngIf="form.voiceName && !voices.includes(form.voiceName)" [value]="form.voiceName">{{ form.voiceName }} (not in the speech model)</option>
                            </select>
                            <ng-template #voiceText>
                                <input id="ag-voice-name" name="voiceName" [(ngModel)]="form.voiceName" maxlength="60" placeholder="Default voice" [disabled]="!form.voiceOutputEnabled" />
                            </ng-template>
                            <span class="hint">Used for voice replies; the voices come from the default speech model (Capability models).</span>
                        </div>
                    </div>
                    <div class="two-col">
                        <label class="check-row">
                            <input type="checkbox" id="ag-image-input" name="imageInputEnabled" [(ngModel)]="form.imageInputEnabled" />
                            Images (visitors can send pictures)
                            <span class="badge warn" *ngIf="notInPlan('allowImages')">not in the plan</span>
                            <span class="badge warn" *ngIf="form.imageInputEnabled && selectedModel && !selectedModel.supportsVision">model has no vision</span>
                        </label>
                        <label class="check-row">
                            <input type="checkbox" id="ag-documents" name="documentInputEnabled" [(ngModel)]="form.documentInputEnabled" />
                            Documents (visitors can attach pdf, docx, xlsx, txt, md or csv files)
                            <span class="badge warn" *ngIf="notInPlan('allowDocuments')">not in the plan</span>
                        </label>
                        <label class="check-row">
                            <input type="checkbox" id="ag-voice-input" name="voiceInputEnabled" [(ngModel)]="form.voiceInputEnabled" />
                            Voice input (voice messages, transcribed)
                            <span class="badge warn" *ngIf="notInPlan('allowVoiceInput')">not in the plan</span>
                        </label>
                        <label class="check-row">
                            <input type="checkbox" id="ag-voice-output" name="voiceOutputEnabled" [(ngModel)]="form.voiceOutputEnabled" />
                            Voice reply (play button on answers)
                            <span class="badge warn" *ngIf="notInPlan('allowVoiceOutput')">not in the plan</span>
                        </label>
                        <label class="check-row">
                            <input type="checkbox" id="ag-knowledge-files" name="knowledgeFilesEnabled" [(ngModel)]="form.knowledgeFilesEnabled" />
                            Knowledge files (answers use the uploaded files)
                            <span class="badge warn" *ngIf="notInPlan('allowKnowledgeFiles')">not in the plan</span>
                        </label>
                    </div>

                    <div class="form-field">
                        <label for="ag-status">Status</label>
                        <select id="ag-status" name="status" [(ngModel)]="form.status">
                            <option *ngFor="let s of statuses" [ngValue]="s">{{ s }}</option>
                        </select>
                        <span class="hint">Only ACTIVE agents answer on the website. The test chat also works on DRAFT.</span>
                    </div>

                    <div class="two-col">
                        <div class="form-field">
                            <label for="ag-welcome">Welcome message</label>
                            <textarea id="ag-welcome" name="welcomeMessage" rows="4" dir="auto" [(ngModel)]="form.welcomeMessage"></textarea>
                        </div>
                        <div class="form-field">
                            <label for="ag-fallback">Fallback message</label>
                            <textarea id="ag-fallback" name="fallbackMessage" rows="4" dir="auto" [(ngModel)]="form.fallbackMessage"></textarea>
                            <span class="hint">Shown to the visitor when the provider fails.</span>
                        </div>
                    </div>
                </fieldset>

                <div *ngIf="error" class="error-text" id="ag-settings-error">{{ error }}</div>
                <div class="tab-footer" *ngIf="canEdit">
                    <button type="button" class="btn-light" (click)="back()">Cancel</button>
                    <button type="submit" class="btn-primary">{{ isNew ? 'Create agent' : 'Save settings' }}</button>
                </div>
            </form>

            <!-- IDENTITY -->
            <form *ngIf="tab === 'identity'" class="tab-body" (ngSubmit)="saveIdentity()">
                <p class="muted">The system prompt is built from these sections in this order; hard rules are added last. Text can be Arabic or English.</p>
                <fieldset [disabled]="!canEdit" class="plain-fieldset">
                    <div class="form-field" *ngFor="let s of sections">
                        <label [for]="'id-' + s.key">{{ s.label }}</label>
                        <span class="hint">{{ s.hint }}</span>
                        <textarea [id]="'id-' + s.key" [name]="s.key" [rows]="s.rows" dir="auto" [(ngModel)]="identity[s.key]"
                            [class.knowledge]="s.key === 'knowledge'"></textarea>
                        <span class="hint right">{{ identityLength(s.key) | number }} characters</span>
                    </div>
                </fieldset>
                <div *ngIf="identityError || error" class="error-text">{{ identityError || error }}</div>
                <div class="tab-footer" *ngIf="canEdit">
                    <button type="submit" class="btn-primary">{{ isNew ? 'Create agent' : 'Save identity' }}</button>
                </div>
            </form>

            <!-- CHANNELS -->
            <app-ai-agent-channels *ngIf="tab === 'channels' && agent" [agent]="agent"></app-ai-agent-channels>

            <!-- PLAYBOOKS (contract v1.4) -->
            <app-ai-agent-playbooks *ngIf="tab === 'playbooks' && agent" [agent]="agent" [plan]="plan" [model]="agentModel"></app-ai-agent-playbooks>

            <!-- TOOLS: AI employee and tool connections, read-only (contract v1.5) -->
            <app-ai-agent-tools *ngIf="tab === 'tools' && agent" [agent]="agent" [plan]="plan" [model]="agentModel"></app-ai-agent-tools>

            <!-- AUTOMATIONS: triggers and their runs, read-only (contract v1.6) -->
            <app-ai-agent-triggers *ngIf="tab === 'automations' && agent" [agent]="agent" [plan]="plan"></app-ai-agent-triggers>

            <!-- TEST CHAT -->
            <app-ai-agent-test-chat *ngIf="tab === 'test' && agent" [agent]="agent"></app-ai-agent-test-chat>
        </div>
    </div>
</section>
